'use client';

import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import type { Bookmark, Folder } from '@/lib/bookmark/types';
import { DragHandle } from './drag-handle';
import { HandleWorkflowDropdown } from './handle-workflow-dropdown';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useWorkflows } from '@/providers/workflow-provider';
import { useBookmarkCount } from '@/lib/bookmark/hooks/use-bookmark-count';
import { cn } from '@/lib/cn';

interface BookmarkCountProbeProps {
  readonly bookmark: Bookmark;
  readonly onCount: (bookmarkId: string, count: number) => void;
}

const BookmarkCountProbe = ({ bookmark, onCount }: BookmarkCountProbeProps) => {
  const { count, isLoading } = useBookmarkCount(bookmark);

  useEffect(() => {
    if (isLoading) return;
    onCount(bookmark.client_id, typeof count === 'number' ? count : 0);
  }, [count, isLoading, bookmark.client_id, onCount]);

  return null;
};

const FolderSummary = ({ folder }: { readonly folder: Folder }) => {
  const bookmarks = folder.child_bookmarks ?? [];
  const [counts, setCounts] = useState<Record<string, number>>({});

  const onCount = useCallback((bookmarkId: string, count: number) => {
    setCounts((prev) =>
      prev[bookmarkId] === count ? prev : { ...prev, [bookmarkId]: count }
    );
  }, []);

  const allLoaded =
    bookmarks.length > 0 &&
    bookmarks.every((b) => counts[b.client_id] !== undefined);
  const total = bookmarks.reduce(
    (sum, b) => sum + (counts[b.client_id] ?? 0),
    0
  );

  return (
    <span className='text-xs font-normal text-muted-foreground'>
      {bookmarks.map((b) => (
        <BookmarkCountProbe key={b.client_id} bookmark={b} onCount={onCount} />
      ))}
      {bookmarks.length} nyckeltal
      {allLoaded ? ` · ${total} totalt` : ''}
    </span>
  );
};

interface WorkflowAccordionProps {
  readonly workEngagementId: string | null;
  readonly isEditMode: boolean;
  readonly folder: Folder;
  readonly isDashboard: boolean;
  readonly isLastFolder: boolean;
  readonly dragHandleProps: DraggableProvidedDragHandleProps | null;
  readonly children: (
    scrollContainerRef: RefObject<HTMLDivElement | null>
  ) => ReactNode;
}

export function WorkflowAccordion({
  workEngagementId,
  isEditMode,
  folder,
  isDashboard,
  isLastFolder,
  dragHandleProps,
  children,
}: WorkflowAccordionProps) {
  const { accordionType, workflowSettings, toggleAccordion, workEngagements } =
    useWorkflows();

  const currentOpenAccordions =
    workflowSettings.expandedItems[accordionType] ?? [];
  const accordionValue = folder.client_id;
  const isOpen = currentOpenAccordions.includes(accordionValue);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const canScrollLeft = container.scrollLeft > 0;
    const canScrollRight =
      container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
    setScrollState((prev) =>
      prev.canScrollLeft === canScrollLeft &&
      prev.canScrollRight === canScrollRight
        ? prev
        : { canScrollLeft, canScrollRight }
    );
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [isOpen, updateScrollState]);

  const scrollBookmarks = (direction: 1 | -1) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({
      left: direction * container.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };

  const workEngagementNameById = useMemo(() => {
    const map = new Map<string, string>();
    workEngagements?.list?.forEach((w) => {
      const id = w.workEngagement?.id;
      if (id) map.set(id, w.workEngagement?.name ?? '');
    });
    return map;
  }, [workEngagements]);

  const role = useMemo(() => {
    if (!workEngagementId) return 'privat';
    return workEngagementNameById.get(workEngagementId) ?? 'arbetsroll';
  }, [workEngagementId, workEngagementNameById]);

  return (
    <Accordion
      type='multiple'
      value={isOpen ? [accordionValue] : []}
      onValueChange={(values) => {
        toggleAccordion(accordionValue, values.includes(accordionValue));
      }}
      className={cn(
        'flex flex-row border-b bg-surface',
        isLastFolder && 'border-b-transparent'
      )}
    >
      <DragHandle dragHandleProps={dragHandleProps} />
      <AccordionItem
        className='w-full overflow-hidden border-none'
        value={accordionValue}
      >
        <div className='flex items-center justify-between'>
          <AccordionTrigger
            icon={false}
            className='justify-start gap-2 p-4 text-sm hover:no-underline [&[data-state=open]>svg]:rotate-90'
          >
            <ChevronRightIcon className='size-4 shrink-0 text-muted-foreground transition-transform duration-200' />
            <div className='flex items-center gap-2 text-left'>
              <span
                className={cn(
                  'font-medium',
                  !folder.pinned && 'text-muted-foreground'
                )}
              >
                {folder.name}
              </span>
              {isDashboard && (
                <Badge
                  variant='outline'
                  className='font-medium text-muted-foreground'
                >
                  {role.toLowerCase()}
                </Badge>
              )}
              {folder.kind === 'workflow' && <FolderSummary folder={folder} />}
            </div>
          </AccordionTrigger>
          <div className='flex items-center'>
            {isOpen &&
              (scrollState.canScrollLeft || scrollState.canScrollRight) && (
                <div className='flex items-center gap-2 pr-4'>
                  <Button
                    variant='outline'
                    size='icon'
                    disabled={!scrollState.canScrollLeft}
                    onClick={() => scrollBookmarks(-1)}
                  >
                    <span className='sr-only'>Skrolla vänster</span>
                    <ChevronLeftIcon className='size-4' />
                  </Button>
                  <Button
                    variant='outline'
                    size='icon'
                    disabled={!scrollState.canScrollRight}
                    onClick={() => scrollBookmarks(1)}
                  >
                    <span className='sr-only'>Skrolla höger</span>
                    <ChevronRightIcon className='size-4' />
                  </Button>
                </div>
              )}
            <HandleWorkflowDropdown
              workEngagementId={workEngagementId}
              type='folder'
              currentItem={folder}
              isEditMode={isEditMode}
            />
          </div>
        </div>
        <AccordionContent className='p-4 pt-0 [&[data-state=closed]]:animate-none [&[data-state=closed]]:transition-none [&[data-state=open]]:animate-none [&[data-state=open]]:transition-none'>
          {children(scrollContainerRef)}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
